import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Container,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SendHorizontal, CheckCircle } from 'lucide-mui';
import { RootState } from '../../../reducers';
import { sendEmail } from '../../../services/emailService';
import { wrapWithTheme } from '../../email/templates';
import { USER_PACKAGE } from '../../plan/userPackage';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  boxShadow: 'none',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 24,
    backgroundColor: theme.palette.background.default,
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 24,
  '& .MuiButton-endIcon': {
    marginLeft: theme.spacing(1),
  },
}));

const SuccessMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  '& .icon': {
    color: theme.palette.success.main,
    width: 64,
    height: 64,
    marginBottom: theme.spacing(2),
  },
  '& .title': {
    color: theme.palette.success.main,
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  '& .subtitle': {
    color: theme.palette.text.secondary,
  },
}));

interface FormData {
  name: string;
  email: string;
  category: string;
  message: string;
}

interface SupportFormProps {
  formSource?: string;
}

const SupportForm: React.FC<SupportFormProps> = ({ formSource }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.settings);
  const { subscription, usage, marketing } = useSelector(
    (state: RootState) => state.plan
  );
  const { selectedTeam } = useSelector((state: RootState) => state.team);

  // Determine if user is on a paid plan
  const { package: userPackage } = subscription || {};
  const isPaidUser =
    userPackage &&
    userPackage !== USER_PACKAGE.FREE &&
    userPackage !== USER_PACKAGE.FREE_LOGGEDIN;

  // Define categories based on user type
  const supportCategories = isPaidUser
    ? [
        { value: 'Reporting problem', label: 'Reporting problem' },
        { value: 'Product Request', label: 'Product Request' },
        { value: 'Technical Support', label: 'Technical Support' },
        { value: 'Billing Issues', label: 'Billing Issues' },
        { value: 'Cancellations', label: 'Cancellations' },
      ]
    : [
        { value: 'Reporting problem', label: 'Reporting problem' },
        { value: 'Product Request', label: 'Product Request' },
        { value: 'Technical Support', label: 'Technical Support' },
        { value: 'Sales Inquiry', label: 'Sales Inquiry' },
        { value: 'Account Issues', label: 'Account Issues' },
      ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    email: user?.email || '',
    category: '',
    message: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [keywordNotices, setKeywordNotices] = useState<any[]>([]);

  // Define keyword-based notices (scalable for future additions)
  const keywordNoticeConfig = [
    {
      keywords: ['refund', 'refunds', 'money back', 'return money'],
      notice:
        'Help us process this faster - what specific issue led to your refund request?',
      type: 'info' as const,
    },
    {
      keywords: [
        'billing',
        'charged',
        'payment',
        'credit card',
        'invoice',
        'subscription fee',
      ],
      notice:
        'Check Account Settings > Billing first, or include your invoice number.',
      type: 'info' as const,
    },
    {
      keywords: [
        'cancel',
        'unsubscribe',
        'stop subscription',
        'end subscription',
        'dont want',
      ],
      notice:
        'You can cancel your subscription anytime in Account Settings > Billing.',
      type: 'warning' as const,
      hasAction: true,
      actionText: 'Go to Billing',
      actionHandler: () => navigate('/settings/billing'),
    },
  ];

  const validateForm = () => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const emailContent = `
<h2>Support Request from User</h2>

<table style="width: 100%; border-collapse: collapse;">
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Form Source:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formSource || 'Contact Support Form'
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Name:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.name
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Email:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.email
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Category:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.category
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Message:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${formData.message.replace(
          /\n/g,
          '<br />'
        )}</td>
</tr>
${
  marketing?.intent
    ? `
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Initial Intent:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${marketing.intent}</td>
</tr>
`
    : ''
}

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>User Information</strong></td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Plan:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          subscription?.package || 'No subscription'
        } (${subscription?.term || 'N/A'})</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Price:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">$${
          subscription?.price || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Subscription Since:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          subscription?.currentPeriodStartedAt
            ? new Date(subscription.currentPeriodStartedAt).toLocaleDateString()
            : 'N/A'
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Stripe ID:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          subscription?.providerId || 'N/A'
        }</td>
</tr>

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>Usage Statistics</strong></td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Total Uploads:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.uploadCount || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>AI Interactions:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.askAICount || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Video AI Interactions:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.videoAskAICount || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Transcriptions:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.transcriptionCount || 0
        }</td>
</tr>

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>Team Information</strong></td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Name:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          selectedTeam?.name || 'No team'
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Members:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          selectedTeam?.teamSpaceMembers?.length || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Created:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          selectedTeam?.createdAt
            ? new Date(selectedTeam.createdAt).toLocaleDateString()
            : 'N/A'
        }</td>
</tr>

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>System Information</strong></td>
</tr>
<tr>
<td style="padding: 12px 0;"><strong>OS:</strong></td>
<td style="padding: 12px 0;">${window.navigator.platform}</td>
</tr>
<tr>
<td style="padding: 12px 0;"><strong>Browser:</strong></td>
<td style="padding: 12px 0;">${window.navigator.userAgent}</td>
</tr>
</table>
`;

        await sendEmail({
          template: 'genericEmail',
          toEmail: 'support@screenapp.io',
          data: {
            content: wrapWithTheme(emailContent),
            subject: `[${formData.category}] [${
              subscription?.package || 'FREE'
            }] Support Request from ${formData.name}`,
          },
        });

        // Send confirmation email to the user
        const confirmationEmailContent = `
<h2>Thank you for contacting ScreenApp Support!</h2>

<p>We have received your support request and will get back to you within 1 working day.</p>

<hr style="margin: 24px 0; border: none; border-top: 1px solid #eee;" />

<h3>Your Support Request Details:</h3>
<table style="width: 100%; border-collapse: collapse;">
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Category:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.category
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Name:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.name
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Email:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.email
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee; vertical-align: top;"><strong>Message:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${formData.message.replace(
          /\n/g,
          '<br />'
        )}</td>
</tr>
</table>

<hr style="margin: 24px 0; border: none; border-top: 1px solid #eee;" />

<p style="color: #666; font-size: 14px;">
If you have any additional questions, please reply to this email or visit our
<a href="https://screenapp.io" style="color: #007bff;">website</a>.
</p>

<p style="color: #666; font-size: 14px;">
Best regards,<br />
The ScreenApp Support Team
</p>
`;

        await sendEmail({
          template: 'genericEmail',
          toEmail: formData.email,
          data: {
            content: wrapWithTheme(confirmationEmailContent),
            subject: `Support Request Received - ${formData.category}`,
          },
        });

        setIsSuccess(true);
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          category: '',
          message: '',
        });
      } catch (error) {
        console.error('Failed to submit form:', error);
        setSnackbar({
          open: true,
          message: 'Failed to submit form. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Check for keyword notices when message field changes
    if (name === 'message') {
      const detectedNotices: any[] = [];
      const messageLower = value.toLowerCase();

      keywordNoticeConfig.forEach((config) => {
        const hasKeyword = config.keywords.some((keyword) =>
          messageLower.includes(keyword.toLowerCase())
        );
        if (hasKeyword) {
          detectedNotices.push(config);
        }
      });

      setKeywordNotices(detectedNotices);
    }
  };

  if (isSuccess) {
    return (
      <Container maxWidth="md">
        <StyledPaper>
          <SuccessMessage>
            <CheckCircle className="icon" />
            <Typography variant="h4" className="title">
              Thank You for Contacting Support!
            </Typography>
            <Typography variant="body1" className="subtitle">
              We have received your message and will get back to you within 1
              working day.
            </Typography>
          </SuccessMessage>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledPaper>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                required
                size="small"
                error={!!errors.category}
              >
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={(e) =>
                    handleChange(e as React.ChangeEvent<HTMLInputElement>)
                  }
                  sx={{
                    borderRadius: 3,
                    backgroundColor: 'background.default',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  {supportCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    {errors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                required
                fullWidth
                label="Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message}
                size="small"
              />
              {keywordNotices.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {keywordNotices.map((config, index) => (
                    <Alert
                      key={index}
                      severity={config.type}
                      sx={{
                        mt: index > 0 ? 1 : 0,
                        '& .MuiAlert-message': {
                          fontSize: '0.875rem',
                        },
                      }}
                      action={
                        config.hasAction ? (
                          <Button
                            color="inherit"
                            size="small"
                            onClick={config.actionHandler}
                          >
                            {config.actionText}
                          </Button>
                        ) : null
                      }
                    >
                      {config.notice}
                    </Alert>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <StyledButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  endIcon={<SendHorizontal />}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </StyledButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </StyledPaper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SupportForm;
